// Which `lang` text needs when it sits in a page of another language (WCAG
// 3.1.2, Language of Parts).
//
// A podcaster's own text (a title, show notes) is in the language of their show,
// and the dashboard around it is in the language of its user. The two are often
// the same. When they are, there is nothing to say: `<html lang>` already does.
// When they are not, the text needs a `lang` of its own, or a screen reader
// reads it in the voice of the page.

import {isLanguageCode} from './languageTag';

/**
 * The tag to put in a `lang` attribute for `language`: hyphens, canonical case
 * (`pt_br` → `pt-BR`). `undefined` when `language` is no language tag, or names
 * no language (`und`), so that a feed with `<language>english</language>` says
 * nothing rather than something that is not valid.
 *
 * A podcast's `language` is whatever the podcaster chose, or whatever
 * `<language>` their imported feed carried. It can be spelled with an
 * underscore, in the wrong case, or not be a language at all ("English", `us`).
 */
export const toLangTag = (
  language: string | null | undefined,
): string | undefined => {
  const tag = (language ?? '').trim().replace(/_/g, '-');
  if (!tag) {
    return undefined;
  }
  let canonical: string | undefined;
  try {
    canonical = Intl.getCanonicalLocales(tag)[0];
  } catch {
    return undefined;
  }
  const primary = canonical?.split('-')[0];
  // A code of ISO 639 that the registry lists (`isLanguageCode`): `us` and `xx`
  // have the shape of a language and are not one, and a page with such a `lang`
  // fails WCAG 3.1.1. The longer primary subtags of the registry would pass
  // `Intl`, and are not something a podcast is in.
  return primary && isLanguageCode(primary) && primary.toLowerCase() !== 'und'
    ? canonical
    : undefined;
};

const primaryOf = (tag: string): string => tag.split('-')[0].toLowerCase();

/**
 * The `lang` for text in `language` on a page in `pageLocale`, or `undefined`
 * when the page's own language is the right one.
 *
 * A regional variant is not another language: `fr-CA` text on a `fr` page, or
 * `pt-PT` on `pt-BR`, is read well enough by the page's voice that a mark would
 * only add noise to every row of every list.
 */
export const langForText = (
  language: string | null | undefined,
  pageLocale: string | null | undefined,
): string | undefined => {
  const tag = toLangTag(language);
  if (!tag) {
    return undefined;
  }
  const page = toLangTag(pageLocale);
  return page && primaryOf(page) === primaryOf(tag) ? undefined : tag;
};
